import { NetworkStatus } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import LoadingButton from "@repo/components/src/shared/loadingButton";
import { Center } from "@repo/components/src/ui/center";
import { HStack } from "@repo/components/src/ui/hstack";
import { Spinner } from "@repo/components/src/ui/spinner";
import { Text } from "@repo/components/src/ui/text";
import { VStack } from "@repo/components/src/ui/vstack";
import { CampaignCommentsDocument } from "@repo/graphql/documents/campaign/query.generated";
import { MontuCommentSentimentChoices } from "@repo/graphql/gql/graphql";
import React, { useMemo, useState } from "react";
import {
  CommentRow,
  SentimentTabs,
  type SentimentFilter,
} from "../../organic/commentParts";
import ExportCommentsButton from "./exportCommentsButton";
import { PostDetailCommentsFilter } from "./types";

// How many comments each page of the post-scoped `comments` query pulls.
const COMMENTS_PAGE_SIZE = 30;

// Maps the sentiment tab to the backend enum; "all" omits the filter so the
// query returns every comment for the post.
const SENTIMENT_BY_FILTER: Record<
  Exclude<SentimentFilter, "all">,
  MontuCommentSentimentChoices
> = {
  pos: MontuCommentSentimentChoices.Positive,
  neu: MontuCommentSentimentChoices.Neutral,
  neg: MontuCommentSentimentChoices.Negative,
};

type Props = {
  filter: PostDetailCommentsFilter;
};

const CommentsAnalyzer = React.memo(({ filter }: Props) => {
  const [sentimentFilter, setSentimentFilter] =
    useState<SentimentFilter>("all");
  const sentiment =
    sentimentFilter === "all"
      ? undefined
      : SENTIMENT_BY_FILTER[sentimentFilter];

  const { data, networkStatus, fetchMore, error } = useQuery(
    CampaignCommentsDocument,
    {
      variables: {
        campaignId: filter.campaignId,
        post: filter.postId,
        sentiment,
        first: COMMENTS_PAGE_SIZE,
      },
      notifyOnNetworkStatusChange: true,
    },
  );

  const connection = data?.post.comments;
  const comments = useMemo(
    () => connection?.edges.map((edge) => edge.node) ?? [],
    [connection],
  );
  const total = connection?.totalCount ?? 0;
  const hasNextPage = connection?.pageInfo.hasNextPage ?? false;

  const loading =
    networkStatus === NetworkStatus.loading ||
    networkStatus === NetworkStatus.setVariables;
  const loadingMore = networkStatus === NetworkStatus.fetchMore;

  const loadMore = () => {
    const pageInfo = connection?.pageInfo;
    if (!pageInfo?.hasNextPage || !pageInfo.endCursor || loadingMore) return;
    fetchMore({ variables: { after: pageInfo.endCursor } });
  };

  // Hidden-when-empty: an error, or a genuinely comment-less post (checked
  // against the unfiltered "all" tab, not whatever sentiment is selected —
  // an empty result for a narrower filter still means "show the empty
  // state", not "hide the analyzer").
  if (error) return null;
  if (!loading && sentimentFilter === "all" && total === 0) return null;

  return (
    <VStack space="sm">
      <HStack className="justify-between items-center flex-wrap" space="sm">
        <Text size="xs" className="text-typography-700 font-semibold uppercase">
          Comentarios
        </Text>
        <HStack space="sm" className="items-center flex-wrap">
          <SentimentTabs
            compact
            value={sentimentFilter}
            onChange={setSentimentFilter}
          />
          {/* Export needs a post to scope to and a brand to authorise
              against; the share route has neither, so it stays hidden. */}
          {filter.brandId && filter.postId ? (
            <ExportCommentsButton
              brandId={filter.brandId}
              postId={filter.postId}
            />
          ) : null}
        </HStack>
      </HStack>
      {loading ? (
        <Center className="py-8">
          <Spinner size="large" />
        </Center>
      ) : comments.length > 0 ? (
        <VStack space="sm">
          {comments.map((c) => (
            <CommentRow key={c.id} comment={c} />
          ))}
          {hasNextPage ? (
            <LoadingButton
              size="xs"
              variant="outline"
              action="secondary"
              rounded="lg"
              onPress={loadMore}
              loading={loadingMore}
              loadingText="Cargando ..."
            >
              Cargar más
            </LoadingButton>
          ) : null}
        </VStack>
      ) : (
        <Text size="sm" className="text-typography-500">
          Sin comentarios en esta categoría.
        </Text>
      )}
    </VStack>
  );
});

CommentsAnalyzer.displayName = "PostDetailCommentsAnalyzer";

export default CommentsAnalyzer;
